import type { FilterMenuRef } from './FilterMenu';
import type { ReactElement, RefObject } from 'react';

import { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { media } from '../../../shared/core/styles';
import { AppliedFilter } from '../AppliedFilter';
import { AppliedSOLRFacets } from '../AppliedSOLRFacets';
import { ClearAllFacets } from '../ClearAllFacets';
import { NUMBER_OF_APPLIED_FILTERS_VARIABLE } from '~/utilities/constants/amplienceVariables';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { useAppliedFacets } from '~/utilities/hooks/search/useAppliedFacets';

const CONFIG = {
  LAYOUT: {
    APPLIED_FILTERS_HEIGHT: 26,
    COLOR_FILTER_SIZE: 12,
    CLEAR_ALL_MIN_HEIGHT: 26,
    ICON_SIZE: 20,
  },
  SCROLL: {
    SCROLL_BEHAVIOR: 'smooth' as const,
  },
  CSS_VARIABLES: {
    APPLIED_FILTERS_TOP_OFFSET: 'calc(var(--smart-banner-height) + var(--top-nav-height) + 54px)',
  },
  Z_INDEX: {
    APPLIED_FILTERS: 3,
    EDGE_FADE: 3,
  },
} as const;

const S = {
  AppliedFilter: styled(AppliedFilter)<{ $isHidden?: boolean }>`
    ${({ $isHidden }) =>
      $isHidden &&
      css`
        opacity: 0;
        pointer-events: none;
        position: absolute;
      `}
  `,

  AppliedSOLRFacets: styled(AppliedSOLRFacets)`
    list-style-type: none;

    &[data-hidden='true'] {
      opacity: 0;
      pointer-events: none;
      position: absolute;
    }
  `,

  AppliedFiltersWrapper: styled.div<{ $isVisible?: boolean }>`
    ${({ theme, $isVisible }) => css`
      box-sizing: border-box;
      display: flex;
      justify-content: flex-end;
      align-items: baseline;
      gap: ${theme.spacing.x['2xs']};
      padding: ${theme.padding.none} ${theme.padding.none}
        ${$isVisible ? theme.padding.sm : theme.padding.none} ${theme.padding.none};
      background: ${theme.colors.background.base};
      overflow: hidden;
      z-index: ${CONFIG.Z_INDEX.APPLIED_FILTERS};

      --filter-top-offset: ${CONFIG.CSS_VARIABLES.APPLIED_FILTERS_TOP_OFFSET};

      position: sticky;
      top: var(--filter-top-offset);

      #uspBar ~ * & {
        top: calc(var(--filter-top-offset) + var(--usp-bar-height));
      }

      @media ${media(theme).greaterThan('sm')} {
        padding: ${theme.padding.none} ${theme.padding.sm} ${theme.padding.none}
          ${theme.padding.none};
      }

      margin: 0;
      width: 100%;
      height: ${CONFIG.LAYOUT.APPLIED_FILTERS_HEIGHT}px;
      flex: 1;
      min-width: 0;

      .colorFilter {
        width: ${CONFIG.LAYOUT.COLOR_FILTER_SIZE}px;
        height: ${CONFIG.LAYOUT.COLOR_FILTER_SIZE}px;
        border-radius: ${theme.borders.radius.xs};
      }
    `}
  `,

  AppliedFilterList: styled.ul`
    ${({ theme }) => css`
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      overflow-x: auto;
      scrollbar-width: none;
      width: 100%;
      scroll-behavior: ${CONFIG.SCROLL.SCROLL_BEHAVIOR};
      gap: ${theme.size[2]};
      margin: 0;
      padding: 0;

      @media ${media(theme).greaterThan('sm')} {
        justify-content: flex-end;
        overflow-x: hidden;
      }

      &::before,
      &::after {
        content: '';
        position: absolute;
        width: ${CONFIG.LAYOUT.APPLIED_FILTERS_HEIGHT}px;
        height: ${CONFIG.LAYOUT.APPLIED_FILTERS_HEIGHT}px;
        pointer-events: none;
        z-index: ${CONFIG.Z_INDEX.EDGE_FADE};
        opacity: 0;

        @media not (prefers-reduced-motion: reduce) {
          transition: opacity 150ms ease;
        }

        @media ${media(theme).greaterThan('sm')} {
          display: none;
        }
      }

      &::before {
        left: 0;
        top: 0;
        background: linear-gradient(90deg, ${theme.colors.background.base} 0%, transparent 100%);
      }

      &::after {
        right: 0;
        top: 0;
        background: linear-gradient(90deg, transparent 0%, ${theme.colors.background.base} 100%);
      }

      &[data-fade-left='true']::before {
        opacity: 1;
      }

      &[data-fade-right='true']::after {
        opacity: 1;
      }

      li {
        flex: 0 0 auto;
        width: fit-content;
        height: ${CONFIG.LAYOUT.APPLIED_FILTERS_HEIGHT}px;
        border-radius: ${theme.borders.radius.xs};

        button {
          height: 100%;
        }
      }

      li:last-child {
        margin-right: ${theme.spacing.x.xs};

        @media ${media(theme).greaterThan('sm')} {
          margin-right: 0;
        }
      }

      li[data-clear-all-placement='desktop'] {
        display: none;
      }

      @media ${media(theme).greaterThan('sm')} {
        li[data-clear-all-placement='mobile'] {
          display: none;
        }

        li[data-clear-all-placement='desktop'] {
          display: block;
        }
      }
    `}
  `,

  ClearAllFacets: styled(ClearAllFacets)<{ $isMobile?: boolean }>`
    ${({ theme, $isMobile }) => css`
      background: transparent;
      min-height: ${CONFIG.LAYOUT.CLEAR_ALL_MIN_HEIGHT}px;

      &:hover,
      &:focus {
        background: ${theme.colors.background.base};
      }

      span {
        display: flex;
        align-items: center;
        height: 100%;
        margin: 0 0 2px ${$isMobile ? 0 : theme.padding.xs};
        color: ${theme.colors.content.tertiary};
        font-size: ${theme.font.size[3]};
        font-weight: ${theme.variables.font.weight.medium};
        line-height: ${theme.size[4]};
        text-decoration: underline;
        text-decoration-thickness: 2px;
        text-underline-offset: 5px;
      }

      div {
        display: none;
      }
    `}
  `,

  OverflowButton: styled.button`
    ${({ theme }) => css`
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 40px;
      height: ${CONFIG.LAYOUT.APPLIED_FILTERS_HEIGHT}px;
      padding: ${theme.padding['3xs']} ${theme.padding['2xs']};
      background: ${theme.colors.background.subtle};
      border-radius: ${theme.borders.radius.xs};
      color: ${theme.colors.content.primary};
      font-size: ${theme.font.size[2]};
      font-weight: ${theme.variables.font.weight.medium};
      cursor: pointer;
      flex-shrink: 0;

      &:hover,
      &:focus {
        background-color: ${theme.colors.neutral[200]};
      }

      @media ${media(theme).lessThan('sm')} {
        display: none;
      }
    `}
  `,
};

type AppliedFiltersContainerProps = {
  isMobile: boolean;
  hasOverflow: boolean;
  visibleFiltersCount: number;
  scrollContainerRef: RefObject<HTMLUListElement | null>;
  filterMenuRef: RefObject<FilterMenuRef | null>;
};

export const AppliedFiltersContainer = ({
  isMobile,
  hasOverflow,
  visibleFiltersCount,
  scrollContainerRef,
  filterMenuRef,
}: AppliedFiltersContainerProps): ReactElement => {
  const { appliedSOLRFacets, exclusiveAlgoliaFacets, numberOfAppliedFacets } = useAppliedFacets();
  const { systemTexts } = useProductListContext();

  const sortedExclusiveAlgoliaFacets = useMemo(
    () => exclusiveAlgoliaFacets.toSorted((a, b) => a.attribute.localeCompare(b.attribute)),
    [exclusiveAlgoliaFacets]
  );

  const handleOverflowClick = useCallback(() => {
    if (filterMenuRef.current?.openFiltersOverlay) {
      filterMenuRef.current.openFiltersOverlay();
    }
  }, [filterMenuRef]);

  const shouldShowOverflow = !isMobile && hasOverflow;
  const hiddenCount = shouldShowOverflow ? numberOfAppliedFacets - visibleFiltersCount : 0;
  const overflowButtonLabel = (
    systemTexts?.filterAndSort?.appliedFiltersOverflowLabel ||
    `Show ${NUMBER_OF_APPLIED_FILTERS_VARIABLE} more applied filters`
  ).replace(NUMBER_OF_APPLIED_FILTERS_VARIABLE, hiddenCount.toString());

  return (
    <S.AppliedFiltersWrapper $isVisible={numberOfAppliedFacets > 0}>
      <S.AppliedFilterList ref={scrollContainerRef}>
        {numberOfAppliedFacets > 0 && <S.ClearAllFacets $isMobile placement="mobile" />}
        <S.AppliedSOLRFacets facets={appliedSOLRFacets} visibleFiltersCount={visibleFiltersCount} />
        {sortedExclusiveAlgoliaFacets.map((appliedFacet, index) => {
          const combinedIndex = appliedSOLRFacets.length + index;
          const isHidden = combinedIndex >= visibleFiltersCount;

          return (
            <S.AppliedFilter
              key={appliedFacet.attribute}
              {...appliedFacet}
              $isHidden={isHidden}
              tabIndex={isHidden ? -1 : 0}
              aria-hidden={isHidden}
            />
          );
        })}
        {shouldShowOverflow && hiddenCount > 0 && (
          <li data-overflow="true" style={{ listStyle: 'none' }}>
            <S.OverflowButton
              type="button"
              data-testid="applied-filters-overflow"
              aria-label={overflowButtonLabel}
              aria-haspopup="dialog"
              onClick={handleOverflowClick}
            >
              +{hiddenCount}
            </S.OverflowButton>
          </li>
        )}
        {numberOfAppliedFacets > 0 && <S.ClearAllFacets $isMobile={false} placement="desktop" />}
      </S.AppliedFilterList>
    </S.AppliedFiltersWrapper>
  );
};
